import type { ComponentStore } from "@schemas/shared/componentStoreSchema";

import { listActiveComponentEntries, metadataForComponentEntries } from "./listActiveComponentEntries";
import { isComponentStore, normalizeComponentStore } from "./normalizeComponentStore";
import type { ComponentStoreData } from "./types";

/**
 * Returns component store data containing only active variants. Used when duplicating
 * a launched Content Optimizer step so deactivated variants are not carried over.
 *
 * Accepts either composer `ComponentStoreData` or the step-load API payload (`visible_components`,
 * snake_case fields) because KO `componentStoreData` is often still that shape until the composer writes back.
 *
 * Image `variantMetadata` use the same active-entry enumeration as surviving URLs so indices stay aligned.
 */
export const pickActiveComponentStoreData = (
  componentStoreData: Partial<ComponentStoreData> | Partial<ComponentStore> | null | undefined
): ComponentStoreData | null | undefined => {
  if (componentStoreData == null) {
    return componentStoreData as null | undefined;
  }

  const normalized: ComponentStoreData = isComponentStore(componentStoreData as ComponentStoreData | ComponentStore)
    ? normalizeComponentStore(componentStoreData as ComponentStore)
    : (componentStoreData as ComponentStoreData);

  const sourceMetadata = normalized.variantMetadata ?? {};
  const deactivatedValues = normalized.deactivatedValues ?? {};
  const activeComponents: Record<string, string[]> = {};
  const nextMetadata: ComponentStoreData["variantMetadata"] = {};

  Object.entries(normalized.components ?? {}).forEach(([componentName, rawValues]) => {
    const deactivatedForComponent = new Set(deactivatedValues[componentName] ?? []);
    const entries = listActiveComponentEntries(rawValues ?? [], deactivatedForComponent);
    activeComponents[componentName] = entries.map(({ value }) => value);

    const metadata = metadataForComponentEntries(sourceMetadata[componentName], entries);
    if (metadata) {
      nextMetadata[componentName] = metadata;
    }
  });

  const result: ComponentStoreData = {
    components: activeComponents,
    deactivatedValues: {},
    newValues: {},
  };

  if (Object.keys(nextMetadata).length > 0) {
    result.variantMetadata = nextMetadata;
  }

  return result;
};
